/**
 * PWAInstallBanner - Prompts users to install ParachPay as a PWA
 * Shows when beforeinstallprompt event fires and can be dismissed
 */
import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { canInstall, promptInstall, isAppInstalled } from '../lib/pwa';

const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed this session
    const wasDismissed = sessionStorage.getItem('pwa-banner-dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    // Check if already installed
    if (isAppInstalled()) {
      return;
    }

    // Listen for install availability
    const handleInstallAvailable = () => {
      setShowBanner(true);
    };

    const handleInstalled = () => {
      setShowBanner(false);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);

    // Check if already available
    if (canInstall()) {
      setShowBanner(true);
    }

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  if (!showBanner || dismissed) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-banner"
    >
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl shadow-2xl shadow-emerald-900/30 p-4 border border-emerald-500/20">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm mb-1">
              Install ParachPay App
            </h3>
            <p className="text-emerald-100 text-xs leading-relaxed">
              Add to your home screen for quick access and offline support.
            </p>
          </div>
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        
        {/* Action buttons */}
        <div className="flex gap-2 mt-3">
          <Button
            onClick={handleInstall}
            className="flex-1 bg-white hover:bg-emerald-50 text-emerald-700 font-semibold text-sm h-10 rounded-xl"
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Install Now
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            className="text-emerald-100 hover:text-white hover:bg-white/10 text-sm h-10 rounded-xl px-4"
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
